import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Chip,
    Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * DraftBoard - Toont de snake draft volgorde met picks en huidige beurt.
 * Wordt gebruikt op de Transfermarkt pagina voor deelnemers.
 *
 * @param {object} props
 * @param {object} props.draftConfig - Draft configuratie document (status, draftOrder, currentPickIndex, etc.)
 * @param {Map} props.participantsMap - Map van uid → naam
 * @param {Array} props.draftTransfers - Transfers met type 'Draft'
 * @param {string} props.currentUserUid - UID van de ingelogde gebruiker
 * @param {boolean} props.isMobile - Of het een mobiel scherm is
 */
const DraftBoard = ({ draftConfig, participantsMap, draftTransfers, currentUserUid, isMobile }) => {
    const theme = useTheme();

    if (!draftConfig || !draftConfig.draftOrder || draftConfig.draftOrder.length === 0) {
        return (
            <Alert severity="info" sx={{ mb: 3 }}>
                De draft loting is nog niet uitgevoerd. Wacht tot de beheerder de loting uitvoert.
            </Alert>
        );
    }

    const currentPickIndex = draftConfig.currentPickIndex || 0;
    const isActive = draftConfig.status === 'active';
    const isCompleted = draftConfig.status === 'completed';
    const isMyTurn = isActive && draftConfig.draftOrder[currentPickIndex] === currentUserUid;
    const midpoint = draftConfig.draftOrder.length / 2;

    // Tel hoeveel picks de huidige gebruiker al heeft gedaan
    const myPicksDone = draftTransfers.filter(t => t.to === currentUserUid).length;
    const totalPicksPerParticipant = 2;

    // Bepaal status bericht
    const getStatusMessage = () => {
        if (isCompleted) return { severity: 'success', text: 'De draft is afgerond!' };
        if (!isActive) return { severity: 'info', text: 'De draft is nog niet gestart. Wacht tot de beheerder de draft start.' };
        if (isMyTurn) return { severity: 'warning', text: '🎯 Jij bent aan de beurt! Ga naar het "Draft spelers" tabblad om een speler te kiezen.' };
        const currentTurnName = participantsMap.get(draftConfig.draftOrder[currentPickIndex]) || 'Onbekend';
        return { severity: 'info', text: `${currentTurnName} is aan de beurt (pick ${currentPickIndex + 1} van ${draftConfig.draftOrder.length}).` };
    };

    const status = getStatusMessage();

    return (
        <Box>
            {/* Status banner */}
            <Alert severity={status.severity} sx={{ mb: 3 }}>
                {status.text}
            </Alert>

            {/* Voortgang */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Chip 
                    label={`Pick ${Math.min(currentPickIndex + 1, draftConfig.draftOrder.length)} / ${draftConfig.draftOrder.length}`} 
                    color="primary" 
                    variant="outlined" 
                />
                <Chip 
                    label={`Jouw picks: ${myPicksDone} / ${totalPicksPerParticipant}`} 
                    color={myPicksDone >= totalPicksPerParticipant ? 'success' : 'default'} 
                    variant="outlined" 
                />
                <Chip 
                    label={draftConfig.status === 'not_started' ? 'Nog niet geloot' :
                           draftConfig.status === 'lottery_done' ? 'Geloot' :
                           draftConfig.status === 'active' ? 'Actief' :
                           draftConfig.status === 'completed' ? 'Afgerond' : draftConfig.status}
                    color={isActive ? 'success' : isCompleted ? 'default' : 'warning'}
                    size="small"
                />
            </Box>

            {/* Snake draft volgorde tabel */}
            <Typography variant="h6" gutterBottom>Draft volgorde (Snake)</Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: isMobile ? 50 : 70 }}>Pick</TableCell>
                            <TableCell>Deelnemer</TableCell>
                            <TableCell>Gekozen speler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {draftConfig.draftOrder.map((uid, index) => {
                            const participantName = participantsMap.get(uid) || 'Onbekend';
                            const isCurrentPick = isActive && index === currentPickIndex;
                            const isMyRow = uid === currentUserUid;
                            const draftTransfer = draftTransfers.find(t => t.draftPickIndex === index && t.to !== 'vrije-markt');
                            const isPastPick = index < currentPickIndex;
                            const isSecondHalf = index >= midpoint;

                            return (
                                <TableRow 
                                    key={index}
                                    sx={{
                                        bgcolor: isCurrentPick 
                                            ? 'action.selected' 
                                            : isMyRow 
                                                ? (theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.04)')
                                                : isSecondHalf 
                                                    ? 'action.hover' 
                                                    : 'transparent',
                                        transition: 'background-color 0.3s',
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: isCurrentPick ? 'bold' : 'normal' }}>
                                        {index + 1}
                                        {index === midpoint - 1 && ' ↩'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: isCurrentPick || isMyRow ? 'bold' : 'normal' }}>
                                        {participantName}
                                        {isMyRow && !isCurrentPick && ' (jij)'}
                                        {isCurrentPick && isMyRow && ' 👈 (jij)'}
                                        {isCurrentPick && !isMyRow && ' 👈'}
                                    </TableCell>
                                    <TableCell>
                                        {draftTransfer ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {draftTransfer.photoUrl && (
                                                    <Avatar src={draftTransfer.photoUrl} sx={{ width: 24, height: 24 }} />
                                                )}
                                                <Typography variant="body2">
                                                    {draftTransfer.playerName}
                                                    {draftTransfer.playerClubName && (
                                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                            ({draftTransfer.playerClubName})
                                                        </Typography>
                                                    )}
                                                    {draftTransfer.profitPercentage > 0 && (
                                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                                                            {draftTransfer.profitPercentage}%
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            isPastPick ? '—' : ''
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DraftBoard;
